import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import "./SidebarQBSReports.scss";

import DropDown from "components/uiComponents/DropDown";
import UniversalLoader from "components/uiComponents/UniversalLoader";
import UniversalButton from "components/uiComponents/UniversalButton";
import { ReactComponent as DownloadIcon } from "static/icons/download-02.svg";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import {
  generateExecutionReport,
  getFullExecutionReport,
  getReportFolders,
  getReportCollectionsForFolder,
  colorCollections,
  getCollectionTable,
} from "utils/builder/IScheduleLogic";
import { QBS_Item_Unit_Values } from "utils/builder/ResultClasses";
import { modelIdToKey } from "utils/builder/TemplatesAndCollections";
import { getTableCollection, ElementsAction } from "store/actions/table";
import { useDashboardState } from "components/DashboardContext";
import ModalComponent from "components/modals/ModalComponent";
import DownloadReportModal from "components/modals/DownloadReportModal";
import { showToast, sortArrayByDateKey } from "utils/helpers";

const SidebarQBSReports = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { setIsTableHidden } = useDashboardState();
  
  // Check if current language is Hebrew
  const isHebrew = i18n.language === 'he' || localStorage.getItem('language') === 'he';

  const colorPalette = [
    "#2F80ED", // blue
    "#EB5757", // red
    "#6FCF97", // green
    "#F2994A", // orange
    "#BB6BD9", // purple
    "#27AE60", // darker green
  ];

  const withCollectionColors = (items = []) => {
    const collections = (items || []).map((collection, index) => ({
      ...collection,
      color: collection?.color || colorPalette[index % colorPalette.length],
    }));

    return sortArrayByDateKey(collections, 'title');
  };

  const tt = (key, fallback) => {
    const val = t(key);
    return val === key ? fallback : val;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);

  const openCollection = async (collection, folderName = activeFolder) => {
    dispatch(ElementsAction(null));
    
    const collectionWithProperties = {
      ...collection,
      elements: collection.elements ? [...collection.elements] : [],
      properties: collection.properties && Array.isArray(collection.properties) 
        ? collection.properties 
        : []
    };
    
    const baseId = collectionWithProperties.creationTime || Date.now();
    const uniqueId = `exec-report-${folderName || 'default'}-${collection?.title || 'untitled'}-${baseId}`;
    collectionWithProperties.creationTime = uniqueId;
    
    setSelectedTitle(collection?.title);
    const colored = collectionWithProperties?.color
      ? collectionWithProperties
      : { ...collectionWithProperties, color: colorPalette[0] };
    colorCollections([colored]);
    
    try {
      await dispatch(getTableCollection({ collection: colored, folder: folderName }));
      setIsTableHidden(false);
    } catch (error) {
      showToast(false, tt("report_download_failed", "Failed to open collection table"));
    }
  };


  const loadReports = async (preserveFolder, skipLoading = false) => {
    if (!skipLoading) {
      setIsLoading(true);
    }
    try {
      const data = await getFullExecutionReport();
      const folderNames = await getReportFolders();
      const sortedFolderNames = folderNames.sort((a, b) => a.localeCompare(b));
      const nextFolder = preserveFolder && sortedFolderNames.includes(preserveFolder)
        ? preserveFolder
        : sortedFolderNames[0] || null;

      const nextCollectionsRaw = nextFolder ? await getReportCollectionsForFolder(nextFolder) : [];
      const nextCollections = withCollectionColors(nextCollectionsRaw);

      setReportData(data);
      setFolders(sortedFolderNames);
      setActiveFolder(nextFolder);
      setCollections(nextCollections);
      setSelectedTitle(null);
    } catch (e) {
      setReportData(null);
      setFolders([]);
      setActiveFolder(null);
      setCollections([]);
      setSelectedTitle(null);
    } finally {
      if (!skipLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleFolderChange = (folder) => {
    dispatch(ElementsAction(null));
    
    setCollections([]);
    setSelectedTitle(null);
    
    setActiveFolder(folder);
    getReportCollectionsForFolder(folder)
      .then((nextCollectionsRaw) => {
        const nextCollections = withCollectionColors(nextCollectionsRaw || []);
        setCollections(nextCollections);
        setSelectedTitle(null);
      })
      .catch(() => {
        setCollections([]);
        setSelectedTitle(null);
      });
  };
  

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      await generateExecutionReport();
      await loadReports(null, true);
    } catch (e) {
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadAll = () => {
    setIsDownloadModalVisible(true);
  };

  const handleDownloadConfirm = async (logoFile) => {
    setIsDownloadModalVisible(false);
    setIsDownloading(true);
    try {
      const data = await getFullExecutionReport();
      const folderNames = await getReportFolders();
      if (!folderNames?.length) {
        setIsDownloading(false);
        return;
      }

      let logoBase64 = null;
      if (logoFile) {
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoFile);
        });
      }

      const zip = new JSZip();

      for (const folderName of folderNames) {
        const rawCollections = await getReportCollectionsForFolder(folderName);
        const collectionsForFolder = withCollectionColors(rawCollections || []);
        const buffer = await buildWorkbookForFolder(folderName, collectionsForFolder, logoBase64);
        zip.file(`${folderName || "report"}.xlsx`, buffer);
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "execution-reports.zip");
      
      const budgetItemsCount = folderNames.length;
      const successMessage = t("report_downloaded_success", {
        count: budgetItemsCount,
        plural: budgetItemsCount !== 1 ? 's' : ''
      });
      showToast(
        true,
        successMessage || `The report has been successfully downloaded for ${budgetItemsCount} budget item${budgetItemsCount !== 1 ? 's' : ''}`
      );
    } catch (e) {
      showToast(false, tt("report_download_failed", "Failed to download report"));
    } finally {
      setIsDownloading(false);
    }
  };

  const getColor = (collection, index) => {
    if (collection?.color) return collection.color;
    return colorPalette[index % colorPalette.length];
  };

  const sanitizeSheetName = (name) => {
    if (!name) return t("sheet");
    return name.replace(/[\\/?*\[\]:]/g, "_").substring(0, 31) || t("sheet");
  };

  // Helper function to set cell style
  const setCellStyle = (cell, isBold = false, isCentered = false) => {
    cell.font = { bold: isBold, size: isBold ? 14 : 12 };
    if (isCentered) {
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    }
  };

  // Helper function to merge cells
  const mergeCells = (sheet, startRow, startCol, endRow, endCol) => {
    sheet.mergeCells(startRow, startCol, endRow, endCol);
  };

  // Helper function to get image dimensions from base64 and calculate proportional size
  const getImageDimensions = (base64) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxWidth = 200; // Maximum width in pixels
        const maxHeight = 60; // Maximum height in pixels
        const aspectRatio = img.width / img.height;
        
        let width = img.width;
        let height = img.height;
        
        // Scale down if image is larger than max dimensions, maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          if (width / maxWidth > height / maxHeight) {
            // Width is the limiting factor
            width = maxWidth;
            height = maxWidth / aspectRatio;
          } else {
            // Height is the limiting factor
            height = maxHeight;
            width = maxHeight * aspectRatio;
          }
        }
        
        resolve({ width, height });
      };
      img.onerror = () => {
        // Fallback to default dimensions if image fails to load
        resolve({ width: 200, height: 60 });
      };
      img.src = base64;
    });
  };

  const addSummarySheet = async (workbook, folderName, collectionsForFolder, logoBase64 = null, collectionTotals = []) => {
    const summary = workbook.addWorksheet(t("summary"));
    
    // Set RTL direction for Hebrew
    if (isHebrew) {
      summary.views = [{ rightToLeft: true }];
    }
    
    const budget = reportData?.budgetItemsData?.[folderName];
    
    // Merge A1-A3 for logo
    mergeCells(summary, 1, 1, 3, 1); // Merge A1:A3
    
    // Row 1: Logo in A1-A3 and Date in D2-D3
    if (logoBase64) {
      try {
        const imageDimensions = await getImageDimensions(logoBase64);
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: logoBase64.includes('data:image/png') ? 'png' : 'jpeg',
        });
        summary.addImage(imageId, {
          tl: { col: 0, row: 0 }, // Column A (index 0), Row 1 (index 0)
          ext: { width: imageDimensions.width, height: imageDimensions.height },
        });
      } catch (e) {
        console.warn('Failed to add logo to summary sheet', e);
      }
    }
    
    // Date in D1-D2
    const dateRow = summary.getRow(1);
    dateRow.getCell(4).value = t("date"); // D1
    setCellStyle(dateRow.getCell(4), true);
    const dateValueRow = summary.getRow(2);
    dateValueRow.getCell(4).value = reportData?.creationDate 
      ? new Date(reportData.creationDate).toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : ""; // D2
    
    // Row 4: Title "Project quantity calculation: [Project name]"
    const titleRow = summary.getRow(4);
    titleRow.getCell(1).value = `${t("project_quantity_calculation")}: ${reportData?.projectName || ""}`;
    mergeCells(summary, 4, 1, 4, 4); // Merge A4:D4
    setCellStyle(titleRow.getCell(1), true, true);
    
    // Row 5: "Summary Sheet"
    const summaryTitleRow = summary.getRow(5);
    summaryTitleRow.getCell(1).value = t("summary_sheet");
    mergeCells(summary, 5, 1, 5, 4); // Merge A5:D5
    setCellStyle(summaryTitleRow.getCell(1), true, true);
    
    // Row 7-11: Item Details
    const unitMeasurementLabel = budget?.unitMeasurement ? ` (${budget.unitMeasurement})` : '';
    const itemDetails = [
      { label: t("item_number"), value: budget?.itemNumber || "[QBS_Item_Number]" },
      { label: t("item_description"), value: budget?.description || "[QBS_Item_Description]" },
      { label: t("unit"), value: budget?.unit ? `${budget.unit}${unitMeasurementLabel}` : "[QBS_Item_Unit]" },
      { label: t("contract_quantity"), value: "" },
      { label: t("associated_quantity"), value: budget?.totalAssigned !== null && budget?.totalAssigned !== undefined ? budget.totalAssigned : "" },
    ];
    
    itemDetails.forEach((item, index) => {
      const row = summary.getRow(7 + index);
      row.getCell(1).value = item.label; // Column A
      setCellStyle(row.getCell(1), true);
      row.getCell(2).value = item.value; // Column B
    });
    
    // Row 13: Table Headers
    const headerRow = summary.getRow(13);
    headerRow.getCell(1).value = t("date"); // A13 - Changed from "account_number" to "date"
    headerRow.getCell(2).value = t("calculated_quantity"); // B13
    headerRow.getCell(3).value = t("approved_quantity"); // C13
    headerRow.getCell(4).value = t("notes"); // D13
    [1, 2, 3, 4].forEach(col => {
      setCellStyle(headerRow.getCell(col), true, true);
      headerRow.getCell(col).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF29B1A' }
      };
      headerRow.getCell(col).border = {
        top: { style: 'thin', color: { argb: 'FF181818' } },
        left: { style: 'thin', color: { argb: 'FF181818' } },
        bottom: { style: 'thin', color: { argb: 'FF181818' } },
        right: { style: 'thin', color: { argb: 'FF181818' } }
      };
    });
    
    // Rows 14+: Selection Sets data (dynamic based on collectionsForFolder.length)
    let calculatedTotal = 0;
    const dataStartRow = 14;
    for (let index = 0; index < collectionsForFolder.length; index++) {
      const collection = collectionsForFolder[index];
      const dataRow = summary.getRow(dataStartRow + index);
      dataRow.getCell(1).value = collection?.title || ""; // A
      
      // Get calculated quantity from collectionTotals (calculated from Selection Set Sheet total)
      // This is the total Calculated Quantity from the corresponding Selection Set Sheet
      const quantity = collectionTotals[index] || 0;
      calculatedTotal += quantity;
      dataRow.getCell(2).value = quantity > 0 ? quantity : ""; // B - Calculated Quantity from Selection Set Sheet total
      dataRow.getCell(3).value = ""; // C - Approved Quantity (empty)
      dataRow.getCell(4).value = ""; // D - Notes (empty)
      
      [1, 2, 3, 4].forEach(col => {
        dataRow.getCell(col).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        dataRow.getCell(col).border = {
          top: { style: 'thin', color: { argb: 'FF181818' } },
          left: { style: 'thin', color: { argb: 'FF181818' } },
          bottom: { style: 'thin', color: { argb: 'FF181818' } },
          right: { style: 'thin', color: { argb: 'FF181818' } }
        };
      });
    }
    
    // Total row: dynamic position after data rows
    const totalRowNumber = dataStartRow + collectionsForFolder.length;
    const totalRow = summary.getRow(totalRowNumber);
    totalRow.getCell(1).value = t("total");
    setCellStyle(totalRow.getCell(1), true, true);
    totalRow.getCell(2).value = calculatedTotal;
    totalRow.getCell(3).value = 0;
    totalRow.getCell(4).value = "";
    [1, 2, 3, 4].forEach(col => {
      totalRow.getCell(col).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF95B3D7' }
      };
      totalRow.getCell(col).border = {
        top: { style: 'thin', color: { argb: 'FF181818' } },
        left: { style: 'thin', color: { argb: 'FF181818' } },
        bottom: { style: 'thin', color: { argb: 'FF181818' } },
        right: { style: 'thin', color: { argb: 'FF181818' } }
      };
      if (col > 1) {
        totalRow.getCell(col).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      }
    });
    
    // Signature section: dynamic position after total row
    const signatureStartRow = totalRowNumber + 2; // 2 rows gap after total
    const signatureLabels = [
      `${t("date")}:`,
      `${t("supervisor_signature")}:`,
      `${t("contractor_signature")}:`
    ];
    signatureLabels.forEach((label, index) => {
      const row = summary.getRow(signatureStartRow + index);
      row.getCell(1).value = label;
      setCellStyle(row.getCell(1), true);
    });
    
    // Set column widths
    summary.getColumn(1).width = 25; // Column A
    summary.getColumn(2).width = 30; // Column B
    summary.getColumn(3).width = 20; // Column C
    summary.getColumn(4).width = 20; // Column D
  };

  const addSelectionSetSheet = async (workbook, collection, folderName, logoBase64 = null) => {
    const sheet = workbook.addWorksheet(sanitizeSheetName(collection?.title));
    
    // Set RTL direction for Hebrew
    if (isHebrew) {
      sheet.views = [{ rightToLeft: true }];
    }
    
    const budget = reportData?.budgetItemsData?.[folderName];
    
    // Merge A1-A3 for logo
    mergeCells(sheet, 1, 1, 3, 1); // Merge A1:A3
    
    // Row 1: Logo in A1-A3 and Date in D1-D2
    if (logoBase64) {
      try {
        const imageDimensions = await getImageDimensions(logoBase64);
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: logoBase64.includes('data:image/png') ? 'png' : 'jpeg',
        });
        sheet.addImage(imageId, {
          tl: { col: 0, row: 0 }, // Column A (index 0), Row 1 (index 0)
          ext: { width: imageDimensions.width, height: imageDimensions.height },
        });
      } catch (e) {
        console.warn('Failed to add logo to selection set sheet', e);
      }
    }
    
    // Date in D1-D2
    const dateRow = sheet.getRow(1);
    dateRow.getCell(4).value = t("date"); // D1
    setCellStyle(dateRow.getCell(4), true);
    const dateValueRow = sheet.getRow(2);
    dateValueRow.getCell(4).value = reportData?.creationDate 
      ? new Date(reportData.creationDate).toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : ""; // D2
    
    // Row 4: Title "Project quantity calculation: [Project name]"
    const titleRow = sheet.getRow(4);
    titleRow.getCell(1).value = `${t("project_quantity_calculation")}: ${reportData?.projectName || ""}`;
    mergeCells(sheet, 4, 1, 4, 4); // Merge A4:D4
    setCellStyle(titleRow.getCell(1), true, true);
    
    // Row 5: "[selection_set_name]"
    const selectionSetTitleRow = sheet.getRow(5);
    selectionSetTitleRow.getCell(1).value = collection?.title || "";
    mergeCells(sheet, 5, 1, 5, 4); // Merge A5:D5
    setCellStyle(selectionSetTitleRow.getCell(1), true, true);
    
    // Row 7-11: Item Details
    const unitMeasurementLabel = budget?.unitMeasurement ? ` (${budget.unitMeasurement})` : '';
    const itemDetails = [
      { label: t("item_number"), value: budget?.itemNumber || "[QBS_Item_Number]" },
      { label: t("item_description"), value: budget?.description || "[QBS_Item_Description]" },
      { label: t("unit"), value: budget?.unit ? `${budget.unit}${unitMeasurementLabel}` : "[QBS_Item_Unit]" },
      { label: t("contract_quantity"), value: "" },
      { label: t("associated_quantity"), value: budget?.totalAssigned !== null && budget?.totalAssigned !== undefined ? budget.totalAssigned : "" },
    ];
    
    itemDetails.forEach((item, index) => {
      const row = sheet.getRow(7 + index);
      row.getCell(1).value = item.label; // Column A
      setCellStyle(row.getCell(1), true);
      row.getCell(2).value = item.value; // Column B
    });
    
    // Row 13: Table Headers
    const headerRow = sheet.getRow(13);
    headerRow.getCell(1).value = t("bim_ids"); // A13
    headerRow.getCell(2).value = t("description"); // B13
    headerRow.getCell(3).value = t("calculated_quantity"); // C13
    headerRow.getCell(4).value = t("measured_quantity"); // D13
    [1, 2, 3, 4].forEach(col => {
      setCellStyle(headerRow.getCell(col), true, true);
      headerRow.getCell(col).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF29B1A' }
      };
      headerRow.getCell(col).border = {
        top: { style: 'thin', color: { argb: 'FF181818' } },
        left: { style: 'thin', color: { argb: 'FF181818' } },
        bottom: { style: 'thin', color: { argb: 'FF181818' } },
        right: { style: 'thin', color: { argb: 'FF181818' } }
      };
    });
    
    // Use collection.elements directly to ensure one row per element
    let calculatedTotal = 0;
    const dataStartRow = 14;
    let dataRowsCount = 0;
    
    if (collection && collection.elements && collection.elements.length > 0) {
      // Process each element individually - one row per element
      collection.elements.forEach((element, index) => {
        const dataRow = sheet.getRow(dataStartRow + index);
        
        // BIM ID - get from element.id
        const elementId = element?.id?.revitId || element?.id?.linkId || t("elements_ids");
        dataRow.getCell(1).value = elementId || t("elements_ids"); // A
        
        // Description - get from propertiesValues
        // If no Calculation Group, leave description empty instead of showing "Calculation Group"
        const calculationGroupProp = element?.propertiesValues?.["<$>Calculation_Group"];
        const typeNameProp = element?.propertiesValues?.["Type Name"];
        const descriptionProp = element?.propertiesValues?.["Description"];
        const description = calculationGroupProp?.displayValue || 
                          typeNameProp?.displayValue || 
                          descriptionProp?.displayValue || 
                          ""; // Empty string if no value found, instead of "Calculation Group"
        dataRow.getCell(2).value = description; // B
        
        // Calculated Quantity - get from the first property field of the table (properties[0])
        // This corresponds to the first property column in the table view
        let quantity = 0;
        if (reportData?.reportSelections?.[folderName]) {
          // Find the collection in reportSelections that matches current collection
          const reportCollections = reportData.reportSelections[folderName];
          const matchingCollection = reportCollections?.find(c => c?.title === collection?.title);
          
          if (matchingCollection && matchingCollection.elements) {
            // Find element in reportSelections by key (element.id)
            const elementKey = modelIdToKey(element.id);
            const reportElement = matchingCollection.elements.find(el => {
              const elKey = modelIdToKey(el.id);
              return elKey === elementKey;
            });
            
            // Use properties from matchingCollection (from reportSelections) to ensure consistency
            // Fallback to collection.properties if matchingCollection.properties is not available
            // properties[0] is the first property column in the table
            const propertiesToUse = matchingCollection?.properties || collection?.properties;
            if (reportElement && propertiesToUse?.[0]) {
              const firstPropertyName = propertiesToUse[0]; // First property = first column in table
              const firstProperty = reportElement?.propertiesValues?.[firstPropertyName];

              if (firstPropertyName === QBS_Item_Unit_Values.Unit) {
                quantity = 1; //use the count parameter when QBS_Item_Unit = Unit
              } else if (firstProperty && firstProperty.displayValue != null) {
                // Ensure we get a number if it's numeric, otherwise use the displayValue as-is
                quantity = typeof firstProperty.displayValue === 'number' 
                  ? firstProperty.displayValue 
                  : (typeof firstProperty.displayValue === 'string' && !isNaN(parseFloat(firstProperty.displayValue)))
                    ? parseFloat(firstProperty.displayValue)
                    : 0;
              }
            }
          }
        }
        calculatedTotal += quantity;
        dataRow.getCell(3).value = quantity > 0 ? quantity : ""; // C
        
        // Measured Quantity - empty
        dataRow.getCell(4).value = ""; // D
        
        [1, 2, 3, 4].forEach(col => {
          dataRow.getCell(col).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          dataRow.getCell(col).border = {
            top: { style: 'thin', color: { argb: 'FF181818' } },
            left: { style: 'thin', color: { argb: 'FF181818' } },
            bottom: { style: 'thin', color: { argb: 'FF181818' } },
            right: { style: 'thin', color: { argb: 'FF181818' } }
          };
        });
      });
      dataRowsCount = collection.elements.length;
    }
    // If no elements, dataRowsCount remains 0, so total row will be at dataStartRow
    
    // Total row: dynamic position after data rows
    const totalRowNumber = dataStartRow + dataRowsCount;
    const totalRow = sheet.getRow(totalRowNumber);
    totalRow.getCell(1).value = t("total");
    setCellStyle(totalRow.getCell(1), true, true);
    totalRow.getCell(2).value = 0; // Description total
    totalRow.getCell(3).value = calculatedTotal; // Calculated Quantity total
    totalRow.getCell(4).value = ""; // Measured Quantity (empty)
    [1, 2, 3, 4].forEach(col => {
      totalRow.getCell(col).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF95B3D7' }
      };
      totalRow.getCell(col).border = {
        top: { style: 'thin', color: { argb: 'FF181818' } },
        left: { style: 'thin', color: { argb: 'FF181818' } },
        bottom: { style: 'thin', color: { argb: 'FF181818' } },
        right: { style: 'thin', color: { argb: 'FF181818' } }
      };
      if (col > 1) {
        totalRow.getCell(col).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      }
    });
    
    // Signature section: dynamic position after total row
    const signatureStartRow = totalRowNumber + 2; // 2 rows gap after total
    const signatureLabels = [
      `${t("date")}:`,
      `${t("supervisor_signature")}:`,
      `${t("contractor_signature")}:`
    ];
    signatureLabels.forEach((label, index) => {
      const row = sheet.getRow(signatureStartRow + index);
      row.getCell(1).value = label;
      setCellStyle(row.getCell(1), true);
    });
    
    // Set column widths
    sheet.getColumn(1).width = 25; // Column A
    sheet.getColumn(2).width = 30; // Column B
    sheet.getColumn(3).width = 20; // Column C
    sheet.getColumn(4).width = 20; // Column D
  };

  // Calculate calculatedTotal for a collection (same logic as in addSelectionSetSheet)
  const calculateCollectionTotal = (collection, folderName) => {
    let calculatedTotal = 0;
    
    if (collection && collection.elements && collection.elements.length > 0) {
      collection.elements.forEach((element) => {
        let quantity = 0;
        if (reportData?.reportSelections?.[folderName]) {
          const reportCollections = reportData.reportSelections[folderName];
          const matchingCollection = reportCollections?.find(c => c?.title === collection?.title);
          
          if (matchingCollection && matchingCollection.elements) {
            const elementKey = modelIdToKey(element.id);
            const reportElement = matchingCollection.elements.find(el => {
              const elKey = modelIdToKey(el.id);
              return elKey === elementKey;
            });
            
            const propertiesToUse = matchingCollection?.properties || collection?.properties;
            if (reportElement && propertiesToUse?.[0]) {
              const firstPropertyName = propertiesToUse[0];
              const firstProperty = reportElement?.propertiesValues?.[firstPropertyName];

              if (firstPropertyName === QBS_Item_Unit_Values.Unit) {
                quantity = 1; //use the count parameter when QBS_Item_Unit = Unit
              } else if (firstProperty && firstProperty.displayValue != null) {
                quantity = typeof firstProperty.displayValue === 'number' 
                  ? firstProperty.displayValue 
                  : (typeof firstProperty.displayValue === 'string' && !isNaN(parseFloat(firstProperty.displayValue)))
                    ? parseFloat(firstProperty.displayValue)
                    : 0;
              }
            }
          }
        }
        calculatedTotal += quantity;
      });
    }
    
    return calculatedTotal;
  };

  const buildWorkbookForFolder = async (folderName, collectionsForFolder = [], logoBase64 = null) => {
    const workbook = new ExcelJS.Workbook();
    
    // Calculate totals for each collection before creating Summary Sheet
    const collectionTotals = collectionsForFolder.map(collection => 
      calculateCollectionTotal(collection, folderName)
    );
    
    // Add Summary sheet as first tab (with calculated totals)
    await addSummarySheet(workbook, folderName, collectionsForFolder, logoBase64, collectionTotals);
    
    // Add Selection Set sheets for each collection
    for (const collection of collectionsForFolder) {
      await addSelectionSetSheet(workbook, collection, folderName, logoBase64);
    }
    
    return workbook.xlsx.writeBuffer();
  };

  return (
    <>
      {isDownloading && (
        <div className="loader__container" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
          backgroundColor: 'rgba(255, 255, 255, 0.9)'
        }}>
          <UniversalLoader
            label={tt("loading", "Loading...")}
            labelColor="gray5"
            preloaderColor="black"
            preloaderBgColor="gray5"
            position="center"
          />
        </div>
      )}
      <div className="sidebar-qbs-reports">
        <div className="sidebar-qbs-reports__header">
          {tt("execution_report", "Execution Report")}
        </div>

      <div className="sidebar-qbs-reports__section">
        <DropDown
          category={tt("Folder", "Folder")}
          listTitle={activeFolder || tt("select_folder", "Select folder")}
          list={folders}
          submitClick={handleFolderChange}
          className="sidebar-qbs-reports__dropdown"
        />
      </div>

      <div className="sidebar-qbs-reports__list">
        {(isLoading || isGenerating) && (
          <div className="sidebar-qbs-reports__loader">
            <UniversalLoader
              label={tt("loading", "Loading...")}
              labelColor="gray5"
              preloaderColor="black"
              preloaderBgColor="gray5"
              position="center"
              isWithoutContainerBg
            />
          </div>
        )}

        {!isLoading && !isGenerating && (!collections || !collections.length) && (
          <div className="sidebar-qbs-reports__empty">
            {tt("no_reports", "No reports")}
          </div>
        )}

        {!isLoading && !isGenerating &&
          collections &&
          collections.map((collection, index) => (
            <div
              className={`sidebar-qbs-reports__item ${selectedTitle === collection?.title ? "sidebar-qbs-reports__item--active" : ""}`}
              key={collection?.creationTime || `${activeFolder}-${collection?.title || index}-${index}`}
              onClick={(e) => {
                openCollection(collection, activeFolder);
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="sidebar-qbs-reports__item_left">
                <div
                  className="sidebar-qbs-reports__item_color"
                  style={{ backgroundColor: getColor(collection, index) }}
                />
                <div className="sidebar-qbs-reports__item_name">
                  {collection?.title}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="sidebar-qbs-reports__controls">
        <UniversalButton
          leftIcon={<DownloadIcon />}
          label={tt("download_all_reports", "Download all reports")}
          labelColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
          isFullWidth
          onClick={handleDownloadAll}
          disabled={isLoading || isGenerating || isDownloading}
          isLoading={isDownloading}
        />
        <UniversalButton
          label={tt("generate_report", "Generate report")}
          labelColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
          isFullWidth
          onClick={handleGenerateReport}
          isLoading={isGenerating}
          disabled={isLoading}
        />
      </div>

      <ModalComponent
        isVisible={isDownloadModalVisible}
        onCloseModal={() => setIsDownloadModalVisible(false)}
        size="md"
      >
        <DownloadReportModal
          onClose={() => setIsDownloadModalVisible(false)}
          onDownload={handleDownloadConfirm}
        />
      </ModalComponent>
      </div>
    </>
  );
};

export default SidebarQBSReports;
